import { useForm as useInertiaForm } from "@inertiajs/react"
import type { InertiaFormProps } from "@inertiajs/react"

// Inertia drops empty arrays from FormData, which the backend reads as "not submitted /
// leave unchanged". Submitting a single blank value keeps the key present (as `{ "0": "" }`),
// so the backend clears the association instead.
const withNonEmptyArrays = <T extends object>(data: T): T => {
  const result = { ...data } as Record<string, unknown>
  Object.keys(result).forEach((key) => {
    const value = result[key]
    if (Array.isArray(value) && value.length === 0) {
      result[key] = [""]
    }
  })
  return result as T
}

// Drop-in replacement for Inertia's useForm that normalizes empty arrays on every submit,
// composed with any transform a form sets itself.
export const useForm = <TForm extends object>(data: TForm): InertiaFormProps<TForm> => {
  const form = (useInertiaForm as (values: TForm) => InertiaFormProps<TForm>)(data)
  const applyTransform = form.transform.bind(form)

  form.transform = (callback) => applyTransform((formData) => withNonEmptyArrays(callback(formData)))
  applyTransform((formData) => withNonEmptyArrays(formData))

  return form
}
